import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FormControl from '@material-ui/core/FormControl';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
  DatePickerView,
  KeyboardDatePickerProps,
} from '@material-ui/pickers';
import { TextFieldProps } from '@material-ui/core/TextField';
import { WrapperVariant } from '@material-ui/pickers/wrappers/Wrapper';
import { PropTypes as MuiPropTypes } from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import isEqual from 'date-fns/isEqual';
import { parseISO } from 'date-fns';
import { enGB, nb, sv } from 'date-fns/locale';
import { useSnackbar } from 'notistack';
// NK
import {
  toDate,
  getNextWeekday,
  isWeekend,
  addDays,
  dateFormats,
  isValidDate,
} from '../../utils/dateUtils';
import { useNkStyles } from '../../infrastructure/theme';

const locales = { 'en-GB': enGB, nb, sv };

type LanguageCode = 'en-GB' | 'nb' | 'sv';

interface NkDatePickerProps {
  todayLabel: string;
  invalidDateMessage: string;
  hideTodayButton?: boolean;
  label: string;
  name: string;
  variant?: WrapperVariant;
  inputVariant?: TextFieldProps['variant'];
  disabled?: boolean;
  isValid: boolean;
  min?: any;
  addDaysToMin?: number;
  max?: any;
  addDaysToMax?: number;
  margin?: MuiPropTypes.Margin;
  disabledDates?: Array<typeof Date>;
  value: Date;
  disableWeekends?: boolean;
  minDateOutOfBoundsMessage?: string;
  maxDateOutOfBoundsMessage?: string;
  onChange(param: { target: { value: Date | null } }): void;
  languageCode: LanguageCode;
}

const NkDatePicker = (props: NkDatePickerProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useNkStyles();

  const _margin = props.margin || 'dense';
  const minDate = props.addDaysToMin
    ? addDays(props.min, props.addDaysToMin)
    : new Date(props.min);
  const maxDate = props.addDaysToMax
    ? addDays(props.max, props.addDaysToMax)
    : new Date(props.max);
  const minProps = props.min ? { minDate: minDate } : null;
  const maxProps = { maxDate: props.max ? maxDate : new Date(9999, 11, 31) };
  const disabledDates = props.disabledDates
    ? props.disabledDates.filter((x: any) => !isEqual(x, props.value))
    : null;

  useEffect(() => {
    if (typeof props.disableWeekends !== 'undefined') {
      onValueChange(props.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.disableWeekends]);

  const prevMin = useRef();
  const prevMax = useRef();
  useEffect(() => {
    let reEvaluate = false;
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
    if (!isEqual(parseISO(prevMin.current), parseISO(props.min))) {
      prevMin.current = props.min;
      reEvaluate = true;
    }
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
    if (!isEqual(parseISO(prevMax.current), parseISO(props.max))) {
      prevMax.current = props.max;
      reEvaluate = true;
    }
    if (reEvaluate) {
      onValueChange(props.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.min, props.max]);

  const onValueChange = (value: any) => {
    if (!value || !isValidDate(value)) return;
    let dateValue: Date | null = value ? new Date(value) : new Date();
    if (
      props.disableWeekends &&
      (dateValue.getDay() >= 6 || dateValue.getDay() === 0)
    ) {
      dateValue = getNextWeekday(dateValue);
    }
    if (props.min && toDate(dateValue) < toDate(props.min)) {
      dateValue = minDate;
      enqueueSnackbar(props.minDateOutOfBoundsMessage, {
        variant: 'warning',
        preventDuplicate: true,
      });
    }
    if (props.max && toDate(dateValue) > toDate(props.max)) {
      dateValue = maxDate;
      enqueueSnackbar(props.maxDateOutOfBoundsMessage, {
        variant: 'warning',
        preventDuplicate: true,
      });
    }
    if (disabledDates) {
      if (isDateDisabled(dateValue)) {
        dateValue = new Date(props.value);
      }
    }
    props.onChange({ target: { value: dateValue } });
  };

  const isDateDisabled = (date: any) => {
    if (props.disableWeekends) {
      return isWeekend(date);
    }

    if (disabledDates) {
      return (
        typeof disabledDates.find((x: any) => isEqual(x, date)) !== 'undefined'
      );
    }

    return false;
  };

  const validClasses = classNames({
    [classes.successIcon]: props.isValid,
    [classes.errorIcon]: typeof props.isValid !== 'undefined' && !props.isValid,
  });

  const views: DatePickerView[] = ['year', 'month', 'date'];

  let pickerProps: KeyboardDatePickerProps = {
    value: props.value,
    onChange: (date: any) => onValueChange(date),
    inputVariant: props.inputVariant,
    variant: props.variant,
    autoOk: true,
    views: views,
    format: dateFormats[props.languageCode],
    readOnly: props.disabled,
    disabled: props.disabled,
    ...minProps,
    ...maxProps,
    name: props.name,
    id: props.name,
    shouldDisableDate: isDateDisabled,
    KeyboardButtonProps: {
      classes: {
        root: validClasses,
      },
    },
    invalidDateMessage: props.invalidDateMessage,
  };

  if (!props.variant || props.variant === 'dialog') {
    pickerProps = {
      ...pickerProps,
      // @ts-expect-error ts-migrate(2322) FIXME: Type '{ cancelLabel: any; showTodayButton: boolean... Remove this comment to see the full error message
      cancelLabel: props.cancelLabel,
      showTodayButton: !props.hideTodayButton,
      todayLabel: props.todayLabel,
    };
  }

  return (
    <FormControl fullWidth margin={_margin} className={classes.formControl}>
      <div className={'input-label'}>{props.label}</div>
      <React.Fragment>
        <MuiPickersUtilsProvider
          utils={DateFnsUtils}
          locale={locales[props.languageCode]}
        >
          <KeyboardDatePicker {...pickerProps} />
        </MuiPickersUtilsProvider>
      </React.Fragment>
    </FormControl>
  );
};

NkDatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: function (props: any, propName: any, componentName: any) {
    if (props[propName] === undefined) {
      return new Error(
        'You must supply the prop "value" to NkDatepicker. A "null" is ok, but the prop must be supplied!',
      );
    }
  },
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  width: PropTypes.string,
  required: PropTypes.bool,
  disableWeekends: PropTypes.bool,
  isValid: PropTypes.bool,
  min: PropTypes.any,
  max: PropTypes.any,
  addDaysToMin: PropTypes.number,
  addDaysToMax: PropTypes.number,
  hideTodayButton: PropTypes.bool,
  disabledDates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
  languageCode: PropTypes.string.isRequired,
  minDateOutOfBoundsMessage: PropTypes.string,
  maxDateOutOfBoundsMessage: PropTypes.string,
  invalidDateMessage: PropTypes.string,
  invalidDateFormatMessage: PropTypes.string,
  cancelLabel: PropTypes.string,
  todayLabel: PropTypes.string,
  variant: PropTypes.string,
  inputVariant: PropTypes.string,
  margin: PropTypes.string,
};

export default NkDatePicker;
